import { Typography } from '@mui/material'
import { KeyboardBackspace } from '@mui/icons-material'
import React, { useContext, useEffect, useState } from 'react'
import './ContentType.scss'
import SelectFacet from '../../SearchFacets/SelectFacet'
import ResultPreview from './Results/ResultPreview/ResultPreview'
import { useNavigate } from 'react-router-dom'
import { ContentSearchBar } from './ContentSearchBar/ContentSearchBar'
import { constants } from '../../../lib/constants'
import ResultModal from './Results/ResultModal/ResultModal'
import itemImage from '../../../images/QIDef.png'
import SideNavButtonQIs from './SideNavigation/SideNavButton/SideNavButtonQIs'
import { obtainGroups } from './CTHelpers/GroupHelper'
import { UserContext } from '../../context/UserContext'
import { checkAccess, queryContent } from './CTHelpers/CheckAccess'
import { CTErrorPage, makeNoAccessRows } from './CTHelpers/CTErrorPage'
import Loader from '../../PageLoader'
import ContentManageButton from '../contentManageButton'
import EmbedShoppingCart from '../../ShoppingCart/embedShoppingCart'

// Export the quality investigations details data.
export const QualityInvestigations = ({ ids }) => {
  const CTName = 'Quality Investigation'
  // Set all required states.
  // Set filter data.
  const [filter, setFilter] = useState({
    group: constants.filter.all,
    text: ''
  })
  // Set group data.
  const [groups, setGroups] = useState()
  // Set data to be displayed.
  const [dataToDisplay, setDataToDisplay] = useState()
  // Set all data.
  const [allData, setAllData] = useState()
  // Set if modal need to be shown.
  const [shouldShowModal, setShouldShowModal] = useState(false)
  // Set the selected item.
  const [selectedItem, setSelectedItem] = useState({})
  // Set loading state.
  const [isLoading, setIsLoading] = useState(true)
  // Set site preview data.
  const [sidePreviews, setSidePreviews] = useState()
  // Set if have availability error.
  const [notAvailable, setNotAvailable] = useState()
  // Set if has access or not.
  const [onlyNoAccess, setOnlyNoAccess] = useState(false)
  // Set if have null value error.
  const [hasNullValue, setHasNullValue] = useState(false)
  // Set if have loading error.
  const [hasLoadingError, setHasLoadingError] = useState(false)
  // Fetch the user context.
  const { userRoles, userName, otherDetails } = useContext(UserContext)
  const history = useNavigate()

  // Check node access when ids are available.
  useEffect(() => {
    // Check for Null State
    if (!ids) {
      setHasNullValue(true)
      setIsLoading(false)
    } else {
      const uniqueIds = [...new Set(ids.split(','))]
      // Check for Access
      const promises = uniqueIds.map(i => checkAccess(i))
      // Store what we can show and what needs to hide
      const show = []
      const hide = []
      // Resolving the promises
      Promise.all(promises).then(accessObjs => {
        accessObjs.forEach(obj => {
          // Show data if have access
          if (obj.access) {
            show.push(obj.item)
          } else {
            // Hide data or generate error base on code received
            if (obj.error === 403) {
              hide.push(obj.item)
            }
            if (obj.error === 404) {
              setHasLoadingError(true)
              setIsLoading(false)
            }
          }
        })
      }).then(() => {
        // Generate proper user information.
        const userInfo = userName || otherDetails.name
        // If user have access.
        if (show && show.length > 0) {
          // Build the payload for api.
          const qiVariables = JSON.stringify({
            ids: show
          })
          // Build the query URL string & call to fetch data.
          const qiQuery = `${process.env.REACT_APP_TARGET_URL}/graphql?queryId=${constants.drupalQueryIds.QIQuery}&variables=${qiVariables}`
          queryContent(qiQuery)
            .then(response => {
              if (!response?.data?.nodeQuery?.count) {
                setHasLoadingError(true)
              } else {
                const entities = response.data.nodeQuery.entities
                setAllData(entities)
                setDataToDisplay(entities)
              }
            })
            .catch(() => setHasLoadingError(true))
            .finally(() => setIsLoading(false))
          // Set different states based on retrieve data.
          const groupsData = obtainGroups(show)
          setGroups(groupsData)
          // Hide the necessary data.
          if (hide && hide.length > 0) {
            // Fetch no access html.
            const noAccessRows = makeNoAccessRows(CTName, hide, userInfo)
            setNotAvailable(noAccessRows)
          }
        } else {
          if (!hide || hide.length === 0) {
            setHasLoadingError(true)
            setIsLoading(false)
          } else {
            // Fetch no access html.
            const noAccessRows = makeNoAccessRows(CTName, hide, userInfo)
            setOnlyNoAccess(true)
            setNotAvailable(noAccessRows)
            setIsLoading(false)
          }
        }
      })
    }
  }, [ids, userName, otherDetails.name])

  // Filter the data based on allData and filter.
  useEffect(() => {
    // Filter for groups.
    switch (filter.group) {
      case constants.filter.all:
        setDataToDisplay(allData)
        break
      case constants.filter.none:
        setDataToDisplay(allData.filter(item => !item.group))
        break
      default:
        setDataToDisplay(allData.filter(item => item.group === filter.group))
        break
    }
    // Filter for text.
    if (filter.text.length > 0) {
      setDataToDisplay(data => (data.filter(item => JSON.stringify(item).toLowerCase().indexOf(filter.text.toLowerCase()) > -1)))
    }
  }, [allData, filter])

  // Filter all the types & data.
  const filterType1 = [
    constants.filter.all,
    constants.filter.none,
    ...(new Set(allData?.reduce((acc, entity) => {
      if (entity.group) {
        acc.push(entity.group)
      }
      return acc
    }, [])))
  ]

  const filterData = category => value => {
    setFilter(data => ({
      ...data,
      [category]: value
    }))
  }

  // Create pages for modal.
  const createPages = item => {
    return [
      {
        topics: [{
          title: 'Base Information',
          fields: [
            { title: 'Group', input: item?.groups.join(', ') },
            { title: 'Finding ID', input: item.findingID?.entity?.nid },
            { title: 'Processes', input: item.qiProcess?.map(({ entity }) => entity?.title).join(', ') },
            { title: 'Subprocesses', input: item.qiSubProcess?.map(({ entity }) => entity?.title).join(', ') },
            { title: 'Root Cause(s)', input: item.qiRootCauses?.map(({ entity }) => entity?.title).join(', ') },
            { title: 'Therapeutic Area', input: item.qiTherapeuticArea?.entity?.title },
            { title: 'Level', input: item.qiLevel?.entity?.title },
            { title: 'Project', input: item.qiProject?.entity?.title },
            { title: 'Approved By', input: item.qiApprovedBy?.entity?.title },
            { title: 'Sources', input: item.qiSources?.map(({ entity }) => entity?.title).join(', ') },
            { title: 'Overall Effectiveness', input: item.qiOverallEffectiveness?.entity?.title }
          ]
        }]
      },
      {
        topics: [{
          title: 'Base Information',
          fields: [
            { title: 'Functional Area 1', input: item.qiFunctionalArea1?.entity?.title },
            { title: 'Functional Area 2', input: item.qiFunctionalArea2?.entity?.title },
            { title: 'GXP', input: item.qiGxp?.map(({ entity }) => entity.title).join(', ') },
            { title: 'Originator', input: item.qiOriginator?.entity?.title },
            { title: 'RC Method', input: item.qiRcMethod?.map(({ entity }) => entity.title).join(', ') },
            { title: 'Product or Process', input: item.qiProductOrProcess?.map(({ entity }) => entity.title).join(', ') },
            { title: 'Segment', input: item.qiSegment?.title },
            { title: 'All Segments', input: item.qiAllSegmentsAffected?.map(({ entity }) => entity.title).join(', ') }
          ]
        }]
      },
      {
        topics: [{
          title: 'Date Information',
          fields: [
            { title: 'Creation Date', input: item.qiCreationDate?.value },
            { title: 'Approval Date', input: item.qiApprovalDate?.value },
            { title: 'Date Occured', input: item.qiDateOccurred?.value },
            { title: 'Date Complete', input: item.qiDateComplete?.value },
            { title: 'Due Date', input: item.qiDueDate?.value },
            { title: 'Original Due Date', input: item.qiOriginalDueDate?.value }
          ]
        },
        {
          title: 'Impact Information',
          fields: [
            { title: 'Assessment Approval Date', input: item.qiImpactAssessmentApproval?.value },
            { title: 'Category', input: item.qiImpactCategory?.entity?.entityLabel },
            { title: 'Classification', input: item.qiImpactClassification?.entity?.title },
            { title: 'Score', input: item.qiImpactScore?.entity?.title },
            { title: 'Description', input: item.qiImpactDescription?.entity?.title }
          ]
        }]
      },
      {
        topics: [{
          title: 'People Involved',
          fields: [
            { title: 'Owner', input: item.qiOwner?.entity?.title },
            { title: 'Quality Leader', input: item.qiQualityLeader?.entity?.title },
            { title: 'Issue Reported By', input: item.qiIssueReportedBy?.entity?.title }
          ]
        },
        {
          title: 'Business Information',
          fields: [
            { title: 'Business Unit', input: item.qiBusinessUnit?.entity?.title },
            { title: 'Owning Franchise', input: item.qiOwningFranchise?.entity?.title },
            { title: 'Owning Site Region', input: item.qiOwningSiteRegion?.entity?.title },
            { title: 'Recommended Inventory Level', input: item.qiRecommendedInvLevel?.entity?.title }
          ]
        },
        {
          title: 'Record Information',
          fields: [
            { title: 'Record State', input: item.qiRecordState?.entity?.title },
            { title: 'Recurring Issue Related Records', input: item.qiRecurringIssueRelated?.map(({ entity }) => entity?.title).join(', ') },
            { title: 'Related System Records', input: item.qiRelatedSystemRecords?.map(({ entity }) => entity?.title).join(', ') }
          ]
        }]
      }
    ]
  }

  // Create preview data for multiple qis.
  const createPreviews = data => {
    // Loop through all qis data.
    data.forEach(qi => {
      const currentGroups = []
      // Loop through the group.
      groups.forEach(group => {
        if (group.id === qi.nid) {
          currentGroups.push(group.group)
        }
      })
      qi.groups = currentGroups
    })
    // Return data for preview.
    // Display manage button based on user role.
    return data && data.map((item, i) =>
      <div key={i} className='result-row'>
        <EmbedShoppingCart item={item} image={itemImage} bundle="quality_investigations" />
        <ResultPreview
          title={item.title}
          nid={item.nid}
          naturalKey={item.naturalKey}
          group={item.groups}
          itemImage={itemImage}
          openModalCallback={() => {
            setSelectedItem(item)
            setShouldShowModal(true)
          }}
        />
        <div className='button-wrappers'>
          <SideNavButtonQIs
            audit={item.findingID?.entity?.qiFindingAudit?.entity}
            findingID={item.findingID?.entity?.nid}
            CAPAs={item.CAPAs?.entities}
            setSidePreviews={setSidePreviews}
            QICRSAC={item?.CRSAC}
            QIESP={item?.ESP}
            QILOC={item?.LOC}
            QIDL={item?.DL}
          />
          {
            userRoles.administrator ||
              userRoles.contentAuthor ||
              userRoles.feedEngineer ||
              userRoles.groupAdmin
              ? <ContentManageButton nid={item.nid} />
              : <div />
          }
        </div>
      </div>
    )
  }

  // Display error message if null value received.
  if (hasNullValue && !isLoading) {
    const message = <Typography dangerouslySetInnerHTML={{ __html: constants.errors.nullValue }} align='center' />
    // Return the error page.
    return (
      <CTErrorPage ct={`${CTName}s`} message={message} />
    )
  }

  // Display error message if don't have access.
  if (onlyNoAccess && !isLoading && notAvailable) {
    // Return the error page.
    return (
      <CTErrorPage ct={`${CTName}s`} message={notAvailable} />
    )
  }

  // Display error message if there is some loading error.
  if (hasLoadingError && !isLoading) {
    const message = <Typography dangerouslySetInnerHTML={{ __html: constants.errors.invalidContent }} align='center' />
    // Return the error page.
    return (
      <CTErrorPage ct={`${CTName}s`} message={message} />
    )
  }

  // Show loader if data fetching in progress.
  if (isLoading) {
    // Return the loader.
    return <Loader />
  }

  // Return entire data for display purpose.
  return !isLoading && allData && (
    <div className='content-wrapper'>
      <div className='header-wrapper'>
        <div className='back-button__clickable' onClick={() => { history(-1) }}>
          <KeyboardBackspace /> <Typography>Back</Typography>
        </div>
        <div className='content-title'>
          <Typography variant='h4'>
            {CTName}s
          </Typography>
        </div>
      </div>
      <div className='search-bar-wrapper'>
        <ContentSearchBar
          setFilter={value => setFilter(data => ({
            ...data,
            text: value
          }))}
        />
      </div>
      <div className='result-wrapper'>
        <div className='filter-wrapper--left-column'>
          <SelectFacet
            onChange={value => {
              filterData('group')(value)
            }}
            selectOptions={filterType1}
            title='Group'
          />
        </div>
        <div className='table-wrapper'>
          {createPreviews(dataToDisplay)}
          {notAvailable}
        </div>
        <div className='sidebar-wrapper--right-column'>
          {sidePreviews}
        </div>
      </div>
      {Object.keys(selectedItem).length > 0 &&
        <ResultModal
          isOpen={shouldShowModal}
          onClose={() => setShouldShowModal(false)}
          nid={selectedItem.nid}
          naturalKey={selectedItem.naturalKey}
          title={selectedItem.title}
          pages={createPages(selectedItem)}
        />}
    </div>
  )
}
